import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';

const FrequentlyAskedQuestions = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = [
    { id: 'all', name: 'All Questions', icon: 'List' },
    { id: 'about', name: 'About Us', icon: 'Building2' },
    { id: 'services', name: 'Services', icon: 'Briefcase' },
    { id: 'approach', name: 'Approach', icon: 'Compass' },
    { id: 'getting-started', name: 'Getting Started', icon: 'Rocket' }
  ];

  const faqData = [
    {
      id: 1,
      category: 'about',
      question: 'What does Northstar Analytics do?',
      answer: 'Northstar Analytics helps organisations turn data into a strategic asset. We design, modernise, and govern data platforms while enabling analytics and AI capabilities that drive measurable business outcomes—improved decision-making, operational efficiency, and sustainable growth.'
    },
    {
      id: 2,
      category: 'about',
      question: 'Who do you typically work with?',
      answer: 'We primarily work with mid-sized and scaling organisations, public sector and regulated industries, and leadership teams who recognise the value of data but need clarity, structure, and execution. Our clients range from healthcare and retail to logistics, fintech, and professional services.'
    },
    {
      id: 3,
      category: 'approach',
      question: 'How are you different from a traditional consultancy?',
      answer: 'We combine strategic advisory with hands-on delivery. Rather than producing theoretical reports, we diagnose real organisational data maturity issues, design practical, implementable architectures and governance models, and support execution through clear roadmaps, tooling, and enablement. Our focus is sustainable capability building—not dependency.'
    },
    {
      id: 4,
      category: 'approach',
      question: 'What is your "data maturity" approach?',
      answer: 'Our proprietary frameworks assess data maturity across technology, governance, people, and processes. This allows us to identify gaps limiting insight and trust in data, risks related to compliance, security, and scalability, and high-impact opportunities for quick wins and long-term value. The result is a clear, prioritised improvement roadmap aligned to business objectives.'
    },
    {
      id: 5,
      category: 'services',
      question: 'Do you work with existing data platforms or only new builds?',
      answer: 'Both. We frequently help organisations modernise legacy data warehouses, improve poorly performing BI and analytics environments, introduce governance, quality, and lineage to existing platforms, and migrate or optimise cloud data architectures. Our approach is pragmatic and cost-conscious—working with what you already have wherever possible.'
    },
    {
      id: 6,
      category: 'services',
      question: 'Can you help with AI and advanced analytics?',
      answer: 'Yes. We support organisations across the AI adoption lifecycle, including AI readiness and governance, responsible AI, transparency, and regulatory alignment (including EU requirements), use-case identification and prioritisation, and analytics and automation that deliver tangible business value. We focus on useful, explainable AI, not experimentation for its own sake.'
    },
    {
      id: 7,
      category: 'services',
      question: 'Are your services suitable for non-technical leaders?',
      answer: 'Absolutely. A core part of our value is translating complex data and AI concepts into clear business language. Our workshops and advisory sessions are designed for senior leaders, enabling informed decision-making without requiring technical expertise.'
    },
    {
      id: 8,
      category: 'services',
      question: 'Do you offer training or workshops?',
      answer: 'Yes. We offer executive briefings on data, analytics, and AI strategy, half-day and full-day leadership workshops, and team enablement sessions covering governance, analytics, and platform best practices. All sessions are tailored to your organisation\'s context and maturity.'
    },
    {
      id: 9,
      category: 'getting-started',
      question: 'How long does a typical engagement last?',
      answer: 'Engagements vary depending on scope, but commonly include short diagnostic or strategy engagements (2–4 weeks), roadmap and architecture design phases, and ongoing advisory or fractional leadership support. We are flexible and transparent about timelines, outcomes, and costs from the outset.'
    },
    {
      id: 10,
      category: 'approach',
      question: 'Do you replace internal teams?',
      answer: 'No. We complement and strengthen them. Our goal is to upskill, align, and enable your internal teams so that improvements are sustainable long after our engagement ends.'
    },
    {
      id: 11,
      category: 'getting-started',
      question: 'How do we get started?',
      answer: 'It begins with a conversation. We start by understanding your goals, challenges, and current data landscape. From there, we propose a focused, value-driven next step—whether that\'s a maturity assessment, strategic roadmap, or targeted intervention.'
    }
  ];

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(questionId)) {
        newSet?.delete(questionId);
      } else {
        newSet?.add(questionId);
      }
      return newSet;
    });
  };

  const filteredFAQs = faqData?.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq?.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      faq?.question?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      faq?.answer?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') return faqData?.length;
    return faqData?.filter(faq => faq?.category === categoryId)?.length;
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Frequently Asked Questions | Northstar Analytics</title>
        <meta 
          name="description" 
          content="Find answers to common questions about Northstar Analytics services, approach, and engagement model. Learn about our data strategy consultancy, AI solutions, and how we help organisations transform data into strategic assets." 
        />
        <meta name="keywords" content="FAQ, data strategy questions, analytics consultancy, AI solutions, data maturity, engagement model, Northstar Analytics" />
        <meta property="og:title" content="Frequently Asked Questions | Northstar Analytics" />
        <meta property="og:description" content="Get answers to your questions about Northstar Analytics services, approach, and how we help organisations turn data into strategic assets." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://northstaranalytics.eu/frequently-asked-questions-comprehensive-faq-center" />
      </Helmet>
      <Header />
      <main className="pt-16 lg:pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-brand text-white py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
                <Icon name="HelpCircle" className="w-8 h-8" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Frequently Asked Questions
              </h1>
              <p className="text-xl text-white/90 mb-8">
                Find answers to common questions about our services, approach, and how we help organisations turn data into strategic assets.
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-2xl mx-auto">
                <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                  <Icon name="Search" className="w-5 h-5 text-slate-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Search questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  className="pl-12 pr-4 py-4 w-full text-lg bg-white border-0 shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="bg-white border-b border-slate-200 sticky top-16 lg:top-20 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories?.map(category => (
                <button
                  key={category?.id}
                  onClick={() => setSelectedCategory(category?.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedCategory === category?.id
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  <Icon name={category?.icon} className="w-4 h-4" />
                  <span>{category?.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    selectedCategory === category?.id
                      ? 'bg-white/20 text-white' :'bg-slate-200 text-slate-600'
                  }`}>
                    {getCategoryCount(category?.id)}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ List */}
        <section className="py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {filteredFAQs?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="SearchX" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">
                  No questions found
                </h3>
                <p className="text-slate-500">
                  Try adjusting your search or filter to find what you're looking for.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFAQs?.map((faq, index) => (
                  <div
                    key={faq?.id}
                    className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                  >
                    <button
                      onClick={() => toggleQuestion(faq?.id)}
                      className="w-full px-6 py-5 flex items-start justify-between gap-4 text-left hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                            {index + 1}
                          </span>
                          <span className="text-xs font-medium text-secondary uppercase tracking-wide">
                            {categories?.find(c => c?.id === faq?.category)?.name}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900">
                          {faq?.question}
                        </h3>
                      </div>
                      <Icon 
                        name={expandedQuestions?.has(faq?.id) ? 'ChevronUp' : 'ChevronDown'} 
                        className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1"
                      />
                    </button>
                    
                    {expandedQuestions?.has(faq?.id) && (
                      <div className="px-6 pb-6 pt-2 border-t border-slate-100 animate-slide-up">
                        <p className="text-slate-600 leading-relaxed">
                          {faq?.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-slate-50 py-16 lg:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Icon name="MessageCircle" className="w-12 h-12 text-secondary mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Still have questions?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              We're here to help. Get in touch with our team for personalised answers and guidance.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a
                href="/contact-multi-channel-engagement-hub"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors shadow-md"
              >
                <Icon name="Mail" className="w-5 h-5" />
                Contact Us
              </a>
              <a
                href="/data-maturity-calculator-lead-generation-assessment"
                className="inline-flex items-center gap-2 px-6 py-3 bg-secondary text-white rounded-lg font-semibold hover:bg-secondary/90 transition-colors shadow-md"
              >
                <Icon name="Calculator" className="w-5 h-5" />
                Take Assessment
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default FrequentlyAskedQuestions;